/*
    The Type class provides functions for disambiguously resolving javascript types.
*/
import { objectApplyDefaults } from '../object/objectApplyDefaults.js';
export class Type {
    static typeOf(variable, resolveInteger = false) {
        // javascript returns 'typeof null' and 'typeof function' as "object"; we don't want that here because we care about whether something is actually an object, so we return "null" and "array" accordingly
        const type = (Array.isArray(variable)) ? "array" : (variable === null) ? "null" : typeof variable;
        return (resolveInteger && type === 'number' && Number.isInteger(variable)) ? 'integer' : type;
    }
    /**
     * Checks if a variable is an object
     * @param variable the variable to check
     * @returns true if the variable is an object, false otherwise
     */
    static isObject(variable) {
        return Type.typeOf(variable) === "object";
    }
    /**
     * Checks if a variable is an array
     * @param variable the variable to check
     * @returns true if the variable is an array, false otherwise
     */
    static isArray(variable) {
        return Array.isArray(variable);
    }
    /**
     * Checks if a variable is null
     * @param variable the variable to check
     * @returns true if the variable is null, false otherwise
     */
    static isNull(variable) {
        return variable === null;
    }
    /**
     * Checks if a variable is a string
     * @param variable the variable to check
     * @returns true if the variable is a string, false otherwise
     */
    static isString(variable) {
        return typeof variable === "string";
    }
    /**
     * Checks if a variable is a boolean
     * @param variable the variable to check
     * @returns true if the variable is a boolean, false otherwise
     */
    static isBoolean(variable) {
        return typeof variable === "boolean";
    }
    /**
     * Checks if a variable is a number
     * @param variable the variable to check
     * @returns true if the variable is a number, false otherwise
     */
    static isNumber(variable) {
        return typeof variable === "number";
    }
    /**
     * Checks if a variable is a function
     * @param variable the variable to check
     * @returns true if the variable is a function, false otherwise
     */
    static isFunction(variable) {
        return typeof variable === "function";
    }
    /**
     * Checks if a variable is undefined
     * @param variable the variable to check
     * @returns true if the variable is undefined, false otherwise
     */
    static isUndefined(variable) {
        return typeof variable === "undefined";
    }
    /**
     * Checks if a variable is a symbol
     * @param variable the variable to check
     * @returns true if the variable is a symbol, false otherwise
     */
    static isSymbol(variable) {
        return typeof variable === "symbol";
    }
    /**
     * Checks if a variable is a bigint
     * @param variable the variable to check
     * @returns true if the variable is a bigint, false otherwise
     */
    static isBigInt(variable) {
        return typeof variable === "bigint";
    }
    /**
     * Checks if a variable is a primitive (i.e. string, boolean, or number)
     * @param variable the variable to check
     * @returns true if the variable is a primitive (string, boolean, or number)
     */
    static isPrimitive(variable) {
        const type = Type.typeOf(variable);
        return type === 'string' || type === 'boolean' || type === 'number' || type == 'null';
    }
    /**
     * Checks if a variable is a primitive object (i.e. if the constructor name is "Object")
     * @param variable the variable to check
     * @returns true if the variable is a primitive object, false otherwise
     */
    static isPrimitiveObject(variable) {
        return Type.getConstructorName(variable) === 'Object';
    }
    /**
     * Checks if a variable is a primitive array (i.e. if the constructor name is "Array")
     * @param variable the variable to check
     * @returns true if the variable is a primitive array, false otherwise
     */
    static isPrimitiveArray(variable) {
        return Type.getConstructorName(variable) === 'Array';
    }
    /**
     * Checks if a variable is a primitive iterable (i.e. if the constructor name is "Object" or "Array")
     * @param variable the variable to check
     * @returns true if the variable is a primitive iterable, false otherwise
     */
    static isPrimitiveIterable(variable) {
        const constructorName = Type.getConstructorName(variable);
        return constructorName === 'Array' || constructorName === 'Object';
    }
    /**
     * Returns the constructor name of the specified variable
     * @param variable the variable to check
     * @returns the constructor name of the specified variable
     */
    static getConstructorName(variable) {
        let constructorName = undefined;
        // just in case variable.constructor.name doesn't resolve (i.e. doesn't have that object hierarchy under our "variable"), let's try-catch to suppress errors and worst case we'll just return undefined
        try {
            constructorName = variable.constructor.name;
        }
        catch (e) {
        }
        return constructorName;
    }
    /**
     * Checks if the supplied variables (as arguments, as many as desired) have the same constructor (technically the same constructor name)
     * @param - note: any number of parameters/arguments are supported
     * @returns true if the arguments all have the same constructor name, false otherwise
     */
    static haveSameConstructor(...args) {
        // get first arg's type
        const theConstructorName = Type.getConstructorName(args[0]);
        // return false if any others have a different type
        const N = args.length;
        for (let i = 1; i < N; i++) {
            if (Type.getConstructorName(args[i]) !== theConstructorName) {
                return false;
            }
        }
        // otherwise return true
        return true;
    }
    /**
     * Returns the class hierarchy as an array for the specified variable
     * @param variable the variable for which find the class hierarchy
     * @returns the class hierarchy as an array for the specified variable
     */
    static getConstructorHierarchy(variable) {
        const hierarchy = [];
        const className = Type.getConstructorName(variable);
        if (className !== undefined && className !== '') {
            hierarchy.push(className);
            let parent = Object.getPrototypeOf(variable.constructor);
            while (parent.name !== undefined && parent.name !== '') {
                hierarchy.push(parent.name);
                parent = Object.getPrototypeOf(parent);
            }
        }
        return hierarchy;
    }
    /**
     * Returns true if the variable inherits from the specified constructorName, false otherwise
     * @param variable the variable to check for a constructorName
     * @param constructorName the constructorName to check for
     * @returns true if the variable inherits from the specified constructorName, false otherwise
     */
    static hasConstructorInHierarchy(variable, constructorName) {
        const className = Type.getConstructorName(variable);
        if (className === constructorName) {
            return true;
        }
        if (className !== undefined && className !== '') {
            let parent = Object.getPrototypeOf(variable.constructor);
            while (parent.name !== undefined && parent.name !== '') {
                if (parent.name === constructorName) {
                    return true;
                }
                parent = Object.getPrototypeOf(parent);
            }
        }
        return false;
    }
    /**
     * Checks if the supplied variables (as arguments, as many as desired) have the same type
     * @param - note: any number of parameters/arguments are supported
     * @returns true if the arguments all have the same type, false otherwise
     */
    static areSameType(...args) {
        // get first arg's type
        const theType = Type.typeOf(args[0]);
        // return false if any others have a different type
        const N = args.length;
        for (let i = 1; i < N; i++) {
            if (Type.typeOf(args[i]) !== theType) {
                return false;
            }
        }
        // otherwise return true
        return true;
    }
    /**
     * Clones an element.  Notably, if element is an instance of a class or function, then the reference to that instance or function is simply copied vs walking into the instance or function and attempting to clone members within etc.
     * @param element the element to clone
     * @param hanlder the definition for a handler function which is called on all leaf elements (any element which is not a primitive iterable, i.e. is neither an object nor an array)
     * @returns a clone of the element
     */
    static clone(element, handler) {
        if (!Type.isPrimitiveIterable(element)) {
            if (typeof handler === 'function') {
                return handler.call(null, element);
            }
            else if (typeof handler === 'object') {
                return handler.function.call(handler.scope, element, handler.holdData);
            }
            return element;
        }
        else if (Type.isPrimitiveObject(element)) {
            const obj = {};
            for (const key in element) {
                obj[key] = Type.clone(element[key], handler);
            }
            return obj;
        }
        else {
            const arr = [];
            const N = element.length;
            for (let i = 0; i < N; i++) {
                arr[i] = Type.clone(element[i], handler);
            }
            return arr;
        }
    }
    /**
     * Returns an object which can be naturally serialized into a json string even if the original variable passed contains instances (of classes) etc.
     * Note: for objects and arrays only owned, enumerable properties are walked.
     * @param variable the variable to jsonize
     * @param options an object containing options including a filter function which
     * @returns an object which can be naturally serialized into a json string even if the original variable passed contains instances (of classes) etc.
     */
    static jsonize(variable, options = Type.JsonizeDefaultOptions, _level = 0) {
        if (_level === 0) {
            if (typeof options.filter === 'string') {
                options.filter = new Function('variable', 'level', options.filter);
            }
            objectApplyDefaults(options, Type.JsonizeDefaultOptions);
        }
        const _options = options; // we know it has the full set of JsonizeSettings now (we do this only so things type-resolve nicely below)
        _level = _level + 1;
        // if we don't pass the filter, return a reduced primitive
        if (_options.filter(variable, _level) === false) {
            return Type.reduceToJSONPrimitive(variable);
        }
        const type = Type.typeOf(variable);
        switch (type) {
            case 'array':
                const array = [];
                const N = variable.length;
                for (let i = 0; i < N; i++) {
                    array.push(Type.jsonize(variable[i], _options, _level));
                }
                return array;
            case 'object':
                const object = {};
                // walk enumerable properties
                for (const key in variable) {
                    if (variable.hasOwnProperty(key)) {
                        object[key] = Type.jsonize(variable[key], _options, _level);
                    }
                }
                return object;
            default:
                return Type.reduceToJSONPrimitive(variable);
        }
    }
    /**
     * Reduces any value to a JSON primitive representation.
     * Primitive types (string, boolean, number) are simply returned, while other types will return a string / serialized representation of their type or kind, such as [Kind: BigInt(9007199254740991)]
     * @param value the value to reduce
     * @returns the reduced value
     */
    static reduceToJSONPrimitive(value) {
        if (Type.isPrimitive(value)) {
            return value;
        }
        if (Type.isBigInt(value)) {
            return `[Kind: BigInt(${value.toString()})]`; // prints like: [Kind: BigInt(9007199254740991)]
        }
        if (Type.isSymbol(value)) {
            return `[Kind: ${value.toString()}]`; // prints like: [Kind: Symbol(MySymbol)]  <-- yeah, Symbols actually stringify this way.
        }
        const type = Type.getConstructorName(value);
        if (type === undefined) {
            return `[Kind: Undefined]`;
        }
        else {
            return `[Kind: ${type}]`;
        }
    }
}
Type.JsonizeDefaultOptions = {
    filter: (variable, level) => { return true; }
};
//# sourceMappingURL=Type.js.map